import React from 'react';
import { Search, LayoutGrid, List } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

type ViewMode = 'grid' | 'list';

interface SearchFilterBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

export const SearchFilterBar = ({
  searchQuery, 
  setSearchQuery, 
  activeTab, 
  setActiveTab,
  viewMode,
  setViewMode
}: SearchFilterBarProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col md:flex-row items-center gap-4">
      <div className="relative w-full md:w-96">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
        <Input 
          placeholder={t('search_placeholder')} 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>
      <Tabs 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="w-full md:w-auto"
      >
        <TabsList>
          <TabsTrigger value="all">{t('all_bots')}</TabsTrigger>
          <TabsTrigger value="active">{t('active_bots')}</TabsTrigger>
          <TabsTrigger value="inactive">{t('inactive_bots')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* View Mode Toggle */}
      <div className="flex items-center gap-1 ml-auto border rounded-lg p-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('grid')}
                className={cn(
                  "h-8 w-8 p-0",
                  viewMode === 'grid' && "bg-muted"
                )}
              >
                <LayoutGrid size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('grid_view')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('list')}
                className={cn(
                  "h-8 w-8 p-0",
                  viewMode === 'list' && "bg-muted"
                )}
              >
                <List size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('list_view')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};
